import { AppWindow, Brain, Palette, Share2 } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChatbotConfig } from '../../hooks/useChatbotConfig';
import { useDesignColors } from '../../hooks/useDesignColors';

interface OnboardingStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
}

const steps: OnboardingStep[] = [
  {
    title: 'Configure your AI',
    description: 'To begin your experience, start by setting up your prompt in the AI settings',
    icon: <Brain className="w-8 h-8 text-indigo-400" />,
    path: '/app/settings/ai'
  },
  {
    title: 'Customize the design',
    description: 'Customize your design with your company colors',
    icon: <Palette className="w-8 h-8 text-purple-400" />,
    path: '/app/settings/design'
  },
  {
    title: 'Configure mini-apps',
    description: 'Select the mini-apps that match your needs and configure them',
    icon: <AppWindow className="w-8 h-8 text-pink-400" />,
    path: '/app/settings/mini-apps'
  },
  {
    title: 'Integrate your chatbot',
    description: 'Test your bot and integrate it on your website or use your public link to communicate and generate maximum clients!',
    icon: <Share2 className="w-8 h-8 text-green-400" />,
    path: '/app/settings/widget'
  }
];

interface OnboardingModalProps {
  onClose: () => void;
}

export function OnboardingModal({ onClose }: OnboardingModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateConfigField } = useChatbotConfig();
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { colors } = useDesignColors();

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      if (dontShowAgain && user) {
        // Sauvegarder la préférence de l'utilisateur
        try {
          await updateConfigField('widget_settings', 'showOnboarding', false);
        } catch (error) {
          console.error('Failed to save onboarding preference:', error);
        }
      }
      onClose();
    }
  };

  const handleSkip = async () => {
    if (dontShowAgain && user) {
      // Sauvegarder la préférence de l'utilisateur
      try {
        await updateConfigField('widget_settings', 'showOnboarding', false);
      } catch (error) {
        console.error('Failed to save onboarding preference:', error);
      }
    }
    onClose();
  };

  const goToStep = (path: string) => {
    navigate(path);
    handleNext();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl p-1">
        <div className="bg-black rounded-xl p-8">
          {/* Progress bar */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((_, index) => (
              <React.Fragment key={index}>
                <div 
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index <= currentStep
                      ? 'bg-indigo-500'
                      : 'bg-white/10'
                  }`}
                />
                {index < steps.length - 1 && (
                  <div 
                    className={`h-0.5 flex-1 mx-2 transition-colors ${
                      index < currentStep
                        ? 'bg-indigo-500'
                        : 'bg-white/10'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Content */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              {steps[currentStep].icon}
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">
              {steps[currentStep].title}
            </h3>
            <p className="text-gray-300 text-lg">
              {steps[currentStep].description}
            </p>
          </div>

          {/* Don't show again checkbox */}
          <div className="flex items-center justify-center mb-6">
            <label className="flex items-center gap-2 text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="rounded border-white/10 bg-white/5 text-indigo-500 focus:ring-indigo-500"
              />
              <span>Do not display</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={handleSkip}
              className="px-6 py-3 rounded-xl text-gray-400 hover:text-white transition-colors"
            >
              Skip
            </button>
            <div className="flex gap-3">
              <button
                onClick={() => goToStep(steps[currentStep].path)}
                className="px-6 py-3 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-colors"
              >
                Configure now
              </button>
              <button
                onClick={handleNext}
                className="px-6 py-3 rounded-xl text-white transition-all"
                style={{
                  background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                }}
              >
                {currentStep === steps.length - 1 ? 'Terminer' : 'Suivant'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}